import { render } from 'preact';
import { useState } from 'preact/hooks';
import { BUILD_VERSION } from '../shared/version';
import { MAX_NAME_LENGTH, isRoomCode } from '../shared/protocol';
import { generateBoard } from '../engine';
import { Board } from './board';
import { LocalApp } from './local';
import { sillyName } from '../shared/names';
import { OnlineRoom, readName, requestAutoJoin, saveName } from './online';
import { selectOnFocus } from './widgets';
import { RulesSheet } from './rules';

function roomCodeFromPath(): string | null {
  const m = location.pathname.match(/^\/r\/([A-Za-z]+)\/?$/);
  const code = m?.[1]?.toUpperCase();
  return code && isRoomCode(code) ? code : null;
}

function Home() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState(() => readName() || sillyName());

  async function create() {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/rooms', { method: 'POST' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { code } = (await res.json()) as { code: string };
      // Seat the creator straight away so they're the host and can set up the game.
      if (name.trim()) {
        saveName(name.trim());
        requestAutoJoin(code);
      }
      location.assign(`/r/${code}`);
    } catch (e) {
      setError(`Couldn't create a room (${(e as Error).message}).`);
      setBusy(false);
    }
  }

  return (
    <div class="panel">
      <h2>Start a game</h2>
      <p class="muted">Create a room, then send the link to everyone who's playing.</p>
      <input
        value={name}
        onInput={(e) => setName((e.target as HTMLInputElement).value)}
        maxLength={MAX_NAME_LENGTH}
        placeholder="Your name"
        aria-label="Your name"
        data-testid="home-name"
        onFocus={selectOnFocus}
      />
      <button onClick={create} disabled={busy} data-testid="create-room">
        New online game
      </button>
      <a class="button secondary" href="/local" data-testid="play-local">
        Play on this device
      </a>
      {error && <p class="error">{error}</p>}
    </div>
  );
}

const PREVIEW_BOARD = generateBoard('fixed');

function App() {
  const local = location.pathname.replace(/\/$/, '') === '/local';
  const code = roomCodeFromPath();
  const [rules, setRules] = useState(false);
  return (
    <>
      <header class="top">
        <h1>
          <a href="/">HEXAGON</a>
        </h1>
        <button class="small secondary" data-testid="open-rules" onClick={() => setRules(true)}>
          Rules
        </button>
      </header>
      {rules && <RulesSheet onClose={() => setRules(false)} />}
      {local ? (
        <LocalApp />
      ) : code ? (
        <OnlineRoom code={code} />
      ) : (
        <>
          <Board board={PREVIEW_BOARD} robber={PREVIEW_BOARD.tiles.findIndex((t) => t.terrain === 'desert')} />
          <Home />
        </>
      )}
      <div class="watermark">{BUILD_VERSION}</div>
    </>
  );
}

render(<App />, document.getElementById('app')!);
